import { Cake } from "lucide-react";
import { useAniversariantesDaSemana } from "@/core/hooks/useAniversariantes";
import { DIAS_ABREV, MESES } from "@/shared/lib/datas";

/**
 * Aniversariantes da Semana (pedido do usuário, 26/07/2026): preview na Home
 * (/inicio), mesmo padrão visual de PreviewAgendaHome/PreviewBibliotecaHome.
 * Mostra só o dia do aniversário (nunca idade ou ano de nascimento — nem o
 * RPC que alimenta esta tela devolve essa informação, ver migration 0026).
 * Semana de SEGUNDA a DOMINGO (como a igreja conta; 10/10/2026, migration
 * 0105 — antes era domingo a sábado e, no sábado, quem faz aniversário no
 * domingo ficava de fora). Não precisa de tarefa agendada porque o cálculo é
 * sempre em cima de "hoje" — troca sozinho na segunda-feira. `posicao` = dia da
 * semana da data do aniversário (0 = domingo); como a janela tem 7 dias, cada
 * dia da semana aparece no máximo uma vez, então "hoje" sai de `posicao`.
 * Sem aniversariantes na semana (ou enquanto carrega), a seção fica oculta,
 * como no preview de Comunicados.
 */
export function PreviewAniversariantesHome() {
  const { data: aniversariantes, isLoading } = useAniversariantesDaSemana();
  if (isLoading || !aniversariantes?.length) return null;

  const hoje = new Date();
  // Semana de segunda a domingo: getDay() é 0 no domingo, então (getDay() + 6) % 7 = dias desde a segunda.
  const inicioSemana = new Date(hoje);
  inicioSemana.setDate(hoje.getDate() - ((hoje.getDay() + 6) % 7));
  const fimSemana = new Date(inicioSemana);
  fimSemana.setDate(inicioSemana.getDate() + 6);
  const diaSemana = (d: Date) => (DIAS_ABREV[d.getDay()] ?? "").toLowerCase();
  const mesmoMes = inicioSemana.getMonth() === fimSemana.getMonth();
  const subtitulo = mesmoMes
    ? `${diaSemana(inicioSemana)}, ${inicioSemana.getDate()} a ${diaSemana(fimSemana)}, ${fimSemana.getDate()} de ${MESES[fimSemana.getMonth()]}`
    : `${inicioSemana.getDate()} de ${MESES[inicioSemana.getMonth()]} a ${fimSemana.getDate()} de ${MESES[fimSemana.getMonth()]}`;

  const posicaoHoje = hoje.getDay();

  return (
    <section className="mb-6 rounded-2xl border border-border bg-surface p-4 sm:p-5">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-bold text-text">
          <Cake size={18} className="text-primary" /> Aniversariantes da Semana
        </h2>
        <span className="text-xs text-muted">{subtitulo}</span>
      </div>
      <div className="flex flex-col gap-2">
        {aniversariantes.map((a) => {
          const ehHoje = a.posicao === posicaoHoje;
          return (
            <div
              key={a.pessoa_id}
              className="flex items-center gap-3 rounded-xl border border-border bg-surface p-3 shadow-sm"
            >
              <div
                className={`w-12 shrink-0 rounded-lg py-1 text-center ${
                  ehHoje ? "bg-primary text-on-primary" : "bg-surface-2 text-text"
                }`}
              >
                <div className="text-base font-bold leading-none">{a.dia}</div>
                <div
                  className={`mt-0.5 text-[10px] uppercase tracking-wide ${
                    ehHoje ? "text-on-primary/85" : "text-muted"
                  }`}
                >
                  {ehHoje ? "Hoje" : DIAS_ABREV[a.posicao]}
                </div>
              </div>
              <p className="min-w-0 flex-1 truncate text-sm font-semibold text-text">
                {a.nome_completo}
              </p>
              {ehHoje && (
                <span className="shrink-0 rounded-full bg-warning/15 px-2 py-0.5 text-[10px] font-bold text-warning">
                  🎂 Hoje
                </span>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
